// The offline queue of touches (docs/CONTRACTS.md section 22.9; P4-9; plan section E, "Data on the device"). A touch that
// could not leave the device waits in IndexedDB: ids, codes and the text the person typed, never a thing read from the
// register or a private file, at most MAX_QUEUED. The device made the touch's idempotency key (a ULID, touch.create's
// `clientKey`) before the first send, so a send repeated from here never makes a second touch, and the time it was logged
// (`at`, set when it is queued: an online send keeps the server's own time). The queue is sent, oldest
// first, when the app is open and online (no background sync on iPhone: UserMenu starts `flushOutbox` on load, on `online`
// and every 15 seconds). It is one person's: another user's items are dropped, never sent as this one. A 401 on any API
// call (expiry, revocation) clears it, as the box's Clear-Site-Data does (plan section E).
import { useSyncExternalStore } from 'react'
import type { NewTouch } from '../features/touches/api.ts'
import { ApiError, api, apiPath, onUnauthenticated } from './api.ts'

/** POLICY.offline.maxQueued, written out so the shell does not carry the whole policy (e2e/offline.spec.ts compares them). */
const MAX_QUEUED = 100
const NAME = 'nw-outbox'
const STORE = 'touch'

export interface Queued {
  /** The touch's ULID and the store's key; ULIDs sort by time, so the store reads oldest first. */
  clientKey: string
  /** The signed-in user who logged it. */
  user: number
  queuedAt: string
  body: NewTouch
  /** The code of the API's refusal: such a touch is kept for the person to see and discard, and not sent again. */
  failed?: string
}

// ---------------------------------------------------------------------------------------------------- the keys
const B32 = '0123456789abcdefghjkmnpqrstvwxyz'

/** A ULID in lower-case Crockford base32: 48 bits of time, then 80 random ones (the API's `ClientKey`). */
export function newClientKey(): string {
  let t = Date.now()
  let time = ''
  for (let i = 0; i < 10; i++) {
    time = B32.charAt(t % 32) + time
    t = Math.floor(t / 32)
  }
  return time + Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => B32.charAt(b % 32)).join('')
}

// ---------------------------------------------------------------------------------------------------- the store
let opened: Promise<IDBDatabase> | undefined

function open(): Promise<IDBDatabase> {
  return (opened ??= new Promise<IDBDatabase>((ok, fail) => {
    const r = indexedDB.open(NAME, 1)
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'clientKey' })
    r.onsuccess = () => {
      const db = r.result
      db.onclose = () => (opened = undefined) // the browser cleared the site's storage
      db.onversionchange = () => db.close()
      ok(db)
    }
    r.onerror = () => fail(r.error)
  }).catch((e: unknown) => {
    opened = undefined
    throw e
  }))
}

async function run<T>(mode: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open()
  return new Promise<T>((ok, fail) => {
    const tx = db.transaction(STORE, mode)
    const req = f(tx.objectStore(STORE))
    tx.oncomplete = () => ok(req.result)
    tx.onerror = tx.onabort = () => fail(tx.error)
  })
}

// ---------------------------------------------------------------------------------------------------- what changed
let count = 0
let version = 0
const listeners = new Set<() => void>()
const tabs = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(NAME)

/** Reads the count and tells every listener (and the other tabs) that the queue may have changed. */
function changed(tell = true): void {
  void run('readonly', (s) => s.count()).then(
    (n) => {
      count = n
      version++
      listeners.forEach((l) => l())
    },
    () => {},
  )
  if (tell) tabs?.postMessage(0)
}
if (tabs) tabs.onmessage = () => changed(false)

const subscribe = (l: () => void) => {
  if (listeners.size === 0) changed(false)
  listeners.add(l)
  return () => void listeners.delete(l)
}

/** How many touches wait in this device's queue (frozen signature, CONTRACTS.md 22.9). */
export function useOutboxCount(): number {
  return useSyncExternalStore(subscribe, () => count)
}

/** Moves on every change of the queue: a view of the items reads them again when it moves. */
export const useOutboxVersion = (): number => useSyncExternalStore(subscribe, () => version)

/** The items, oldest first. Rejects when the store cannot be read. */
export const outboxList = (): Promise<Queued[]> => run('readonly', (s) => s.getAll() as IDBRequest<Queued[]>)

/** The count now, from the store itself (another tab may have queued since this one last looked); 0 when unreadable. */
export const outboxCount = (): Promise<number> => run('readonly', (s) => s.count()).catch(() => 0)

// ---------------------------------------------------------------------------------------------------- writing
let owner: number | null = null

/**
 * Puts a touch that did not reach the server in the queue, under the signed-in user. False when it cannot wait here: the
 * queue is full, the browser has no storage, or nobody is signed in; the caller then fails the write as before.
 * shortcut: two tabs can pass the limit together by one or two items, when the limit is worth a transaction.
 */
export async function outboxAdd(body: NewTouch): Promise<boolean> {
  if (owner === null || !body.clientKey) return false
  try {
    if ((await run('readonly', (s) => s.count())) >= MAX_QUEUED) return false
    const queuedAt = new Date().toISOString()
    // The touch happened now, not when a later send reaches the server (iPhone has no background sync): it carries its own time.
    const item: Queued = { clientKey: body.clientKey, user: owner, queuedAt, body: { ...body, at: body.at ?? queuedAt } }
    await run('readwrite', (s) => s.put(item))
    changed()
    return true
  } catch {
    return false
  }
}

/** Drops one item (the person discarded it), or all of them. */
export async function outboxDiscard(clientKey?: string): Promise<void> {
  await run('readwrite', (s) => (clientKey ? s.delete(clientKey) : s.clear()))
  changed()
}

// A 401 anywhere: the session is over, and what it queued goes with it.
onUnauthenticated(() => void outboxDiscard().catch(() => {}))

// ---------------------------------------------------------------------------------------------------- sending
let flushing: Promise<number> | null = null

/** A failure that says nothing about the touch itself: the connection, the server (any 5xx, the API's own 500 `internal` included), the rate limit or the session, now. The touch stays queued and is tried again. */
const later = (e: ApiError) => e.code === 'offline' || e.code === 'unavailable' || e.code === 'bad_response' || e.status === 429 || e.status === 401 || e.status >= 500

/**
 * Sends what waits for `user`, oldest first, and answers how many the server took. It also records whose queue this is:
 * items of another user are removed unsent. A refusal that is about the touch (the API's own 4xx) marks it `failed` and goes on;
 * a connection, any 5xx, a 429 or a 401 stops the run with everything still queued. Safe to call at any time and from
 * several tabs: a touch sent twice carries one key and is one touch.
 */
export function flushOutbox(user: number): Promise<number> {
  owner = user
  return (flushing ??= send(user).finally(() => (flushing = null)))
}

async function send(user: number): Promise<number> {
  if (!navigator.onLine) return 0
  let sent = 0
  let touched = false
  try {
    for (const q of await outboxList()) {
      if (q.user !== user) {
        await run('readwrite', (s) => s.delete(q.clientKey))
        touched = true
        continue
      }
      if (q.failed) continue
      try {
        await api.post(apiPath('/api/touches'), q.body)
        await run('readwrite', (s) => s.delete(q.clientKey))
        touched = true
        sent++
      } catch (e) {
        if (!(e instanceof ApiError)) throw e
        if (later(e)) break
        await run('readwrite', (s) => s.put({ ...q, failed: e.code }))
        touched = true
      }
    }
  } catch {
    /* the store could not be read or written: the next call tries again */
  }
  if (touched) changed()
  return sent
}
